import React, { useEffect, useRef } from 'react'
import { Box, Chip, Stack, TextField, Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'

import useRubrics from '../../hooks/useRubrics'
import { scrollBarStyles } from '../../assets/css/scrollbar'
import { ReactComponent as Search } from '../../assets/icons/discover-icon.svg'
import { ReactComponent as StarIcon } from '../../assets/icons/star.svg'
function RubricSearch({ onSelectRubric = () => {}, selectedRubricId = null, type = null }) {
  const queryClient = useQueryClient()
  const {
    profile: { recentRubrics = [], savedRubrics = [], defaultRubricId = null },
    auth: { uid },
  } = useSelector(({ user }) => user)

  const [search, setSearch] = React.useState('')
  const [filter, setFilter] = React.useState('')
  const { rubrics } = useRubrics(search)
  // scroll to selected rubric
  const scrollRef = useRef(null)

  React.useEffect(() => {
    if (selectedRubricId && scrollRef.current) {
      scrollRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [selectedRubricId])

  const filters = [
    {
      filter: 'recent',
      label: 'Recently Used',
      chipColors: {
        defaultState: {
          color: '#344054',
          bgcolor: 'transparent',
          borderColor: '#475467',
        },
        activeState: {
          bgcolor: '#F8F9FC',
          color: '#363F72',
          borderColor: '#D5D9EB',
        },
      },
    },
    {
      filter: 'template',
      label: 'Templates',
      chipColors: {
        defaultState: {
          bgcolor: 'transparent',
          color: '#C11574',
          borderColor: '#DD2590',
        },
        activeState: {
          bgcolor: '#FDF2FA',
          color: '#C11574',
          borderColor: '#FCCEEE',
        },
      },
    },
    {
      filter: 'myOwn',
      label: 'My Own',
      chipColors: {
        defaultState: {
          bgcolor: 'transparent',
          color: '#3538CD',
          borderColor: '#444CE7',
        },
        activeState: {
          bgcolor: '#EEF4FF',
          color: '#3538CD',
          borderColor: '#C7D7FE',
        },
      },
    },
  ]

  if (savedRubrics?.length > 0) {
    filters.push({
      filter: 'saved',
      label: 'Saved',
      chipColors: {
        defaultState: {
          bgcolor: 'transparent',
          color: '#00A86B',
          borderColor: '#00A86B',
        },
        activeState: {
          bgcolor: '#E6F9F5',
          color: '#00A86B',
          borderColor: '#00A86B',
        },
      },
    })
  }
  const isSelector = type === 'selector'

  return (
    <Box
      sx={{
        p: isSelector ? 0 : '16px 0px 16px 16px',
        height: '100%',
      }}>
      {!isSelector ? (
        <Typography fontSize={24} fontWeight={600}>
          Rubric Library
        </Typography>
      ) : null}
      <TextField
        fullWidth
        InputProps={{
          startAdornment: (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                mr: 1,
              }}>
              <Search />
            </Box>
          ),
          sx: {
            fontSize: 16,
          },
        }}
        placeholder="Search rubrics..."
        sx={{
          my: '16px',
          pr: '16px',
        }}
        value={search}
        onChange={e => setSearch(e.target.value)}
      />
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start', // "space-between
          alignItems: 'center',
          p: '0px 16px',
          gap: 1,
        }}>
        {filters.map(f => (
          <FilterChip
            key={f.filter}
            chipColors={f.chipColors}
            color={f.color}
            filter={f.filter}
            label={f.label}
            selected={filter === f.filter}
            onClick={() => {
              setFilter(f.filter === filter ? null : f.filter)
            }}
          />
        ))}
      </Box>
      <Box
        // ref={scrollRef}
        sx={{
          overflowY: 'auto',
          height: '100%',
          pr: 2,
          pb: '200px',
          ...scrollBarStyles,
        }}>
        <Stack mt={2} spacing={2}>
          {rubrics
            .filter(r => {
              if (filter === 'recent') {
                return recentRubrics.includes(r.id)
              } else if (filter === 'template') {
                return r.template
              } else if (filter === 'myOwn') {
                return r?.owners?.includes?.(uid)
              } else if (filter === 'saved') {
                return savedRubrics.includes(r.id)
              } else {
                return true
              }
            })

            .map(rubric => (
              <RubricCard
                key={rubric.id}
                isDefault={defaultRubricId === rubric?.id}
                isOwner={rubric?.owners?.includes?.(uid)}
                rubric={rubric}
                selected={selectedRubricId === rubric?.id}
                onClick={onSelectRubric}
              />
            ))}
        </Stack>
      </Box>
    </Box>
  )
}

export default RubricSearch

export function RubricCard({ rubric, onClick, selected = false, isOwner, isDefault }) {
  return (
    <Box
      sx={{
        p: '12px 16px',
        borderRadius: '12px',
        border: selected ? '1px solid #EE46BC' : '1px solid #EAECF0',
        cursor: 'pointer',
        '&:hover': {
          bgcolor: 'background.default',
        },
        width: '100%',
        position: 'relative',
      }}
      onClick={() => onClick(rubric)}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}>
        {isDefault ? <StarIcon /> : null}
        <Typography
          fontSize={14}
          fontWeight={500}
          sx={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}>
          {rubric.name}
        </Typography>
      </Box>
      <Typography
        fontSize={12}
        fontWeight={400}
        sx={{
          // max lines 2
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          color: 'text.disabled',
        }}>
        {rubric.description}
      </Typography>
      <Box
        sx={{
          width: '12px',
          height: '12px',
          borderRadius: '50%',
          position: 'absolute',
          top: '12px',
          right: '12px',
          bgcolor: rubric?.template ? '#EE46BC' : isOwner ? '#3538CD' : '',
        }}
      />
    </Box>
  )
}

function FilterChip({ label, selected, onClick, chipColors }) {
  const styles = selected ? chipColors.activeState : chipColors.defaultState

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        ...styles,
        cursor: 'pointer',
      }}
      variant="outlined"
      onClick={onClick}
    />
  )
}
